// src/services/authService.js

import axios from 'axios';
import CONFIG from "../config/config";


export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export interface TokenStorage {
  accessToken: string;
  refreshToken: string;
  expires: number;
}

class AuthService {
  decodeToken(token: string): any {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      console.error("Failed to decode token", error);
      return null;
    }
  }

  getTokenExpirationDate(token: string): Date {
    const decoded = this.decodeToken(token);
    if (!decoded) return new Date();
    return new Date(decoded.exp * 1000);
  }

  isTokenExpired(token: string): boolean {
    const expirationDate = this.getTokenExpirationDate(token);
    return expirationDate !== null ? expirationDate < new Date() : false;
  }

  async login(username: string, password: string) {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await axios.post(`${CONFIG.baseApiUrl}auth/login`, {
        username,
        password
      }, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      if (response.data.statusCodeValue) {
        const statusCode = response.data.statusCodeValue;
        switch (statusCode) {
          case 200:
            if (response.data.body.token && response.data.body.refreshToken) {
              const exp = this.decodeToken(response.data.body.token).exp;
              const phone = this.decodeToken(response.data.body.token).phone;
              const email = this.decodeToken(response.data.body.token).email;
              sessionStorage.setItem('token', JSON.stringify({
                accessToken: response.data.body.token,
                refreshToken: response.data.body.refreshToken,
                expires: Date.now() + exp * 1000,
                lastRefreshTimestamp: Date.now()
              }));
              sessionStorage.setItem('user', username);
              sessionStorage.setItem('phone', phone);
              sessionStorage.setItem('email', email);
            }
            return response.data;
            break;
          case 401:
            throw new Error(`username_password_combination_error`);
            break;
          case 403:
            throw new Error('client__login_locked');
            break;
          case 404:
            throw new Error('Dieser Account existiert nicht.');
            break;
          default:
            throw new Error('Ein unbekannter Fehler ist aufgetreten.');
        }
      }
    } catch (error) {
      throw error;
    }
  }

  async logout() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('phone');
    sessionStorage.removeItem('email');
  }

  async register(username: string, email: string, phone: string, password: string, captchaToken: string): Promise<void> {
    try {
      const response = await axios.post(`${CONFIG.baseApiUrl}auth/register`, {
        username,
        email,
        phone,
        password,
        captchaToken
      }, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      return response.data;
    } catch (error: any) {
      if (error && error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error)
      } else {
        throw new Error('Registration failed');
      }
    }
  }

  async update(username: string, email: string, phone: string, oldPassword: string, password: string): Promise<void> {
    try {
      const response = await axios.post(`${CONFIG.baseApiUrl}auth/update`, {
        username,
        email,
        phone,
        oldPassword,
        password,
      }, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      return response.data;
    } catch (error: any) {
      if (error && error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error)
      } else {
        throw new Error('Update failed');
      }
    }
  }

  async remove(username: string): Promise<void> {
    try {
      const response = await axios.post(`${CONFIG.baseApiUrl}auth/remove`, {
        username,
      }, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      return response.data;
    } catch (error: any) {
      if (error && error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error)
      } else {
        throw new Error('Delete failed');
      }

    }
  }

  async refreshAccessToken(refreshToken: string) {
    try {
      const response = await axios.post(`${CONFIG.baseApiUrl}auth/refresh`, {
        refreshToken
      }, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      if (response.data.token && response.data.refreshToken) {
        const exp = this.decodeToken(response.data.body.token).exp;
        const phone = this.decodeToken(response.data.body.token).phone;
        const email = this.decodeToken(response.data.body.token).email;
        sessionStorage.setItem('token', JSON.stringify({
          accessToken: response.data.token,
          refreshToken: response.data.refreshToken,
          expires: Date.now() + exp * 1000,
        }));
        sessionStorage.setItem('phone', phone);
        sessionStorage.setItem('email', email);
        return response.data.token;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      location.reload();
      throw new Error('Session expired. Please log in again.');
    }
  }

}

export default new AuthService();
